import React, { useState } from "react"
import ReactApexChart from "react-apexcharts"
import { useTranslation } from "react-i18next"
import { Card, Col, Row } from "reactstrap"

type TimeFilter =
  | "dashboard_all"
  | "dashboard_oneMonth"
  | "dashboard_sixMonths"
  | "dashboard_oneYear"

interface SummaryCardProps {
  title: string
  value: string
  change: string
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, change }) => {
  const { t } = useTranslation()

  return (
    <div className="d-flex flex-column">
      <span
        className="text-muted mb-1"
        style={{ fontSize: "12px", color: "#6B7280" }}
      >
        {title}
      </span>
      <div className="d-flex align-items-baseline gap-2">
        <span style={{ fontSize: "20px", fontWeight: 600 }}>
          {value} {t("common_qar")}
        </span>
        <span
          className="d-flex align-items-center"
          style={{ fontSize: "12px", color: "#10B981", fontWeight: 500 }}
        >
          {change}
          <i className="bx bx-up-arrow-alt"></i>
        </span>
      </div>
    </div>
  )
}

const TimeFilterButton: React.FC<{
  label: TimeFilter
  isActive: boolean
  onClick: () => void
}> = ({ label, isActive, onClick }) => {
  const { t, i18n } = useTranslation()

  return (
    <button
      onClick={onClick}
      className={`font-size-12 fw-regular ${
        isActive ? "text-primary bg-primary-30" : "bg-cyan-30 text-cyan"
      }`}
      style={{
        border: "none",
      }}
    >
      {t(label)}
    </button>
  )
}

export const RevenueAndEarnings: React.FC = () => {
  const { t } = useTranslation()
  const [activeFilter, setActiveFilter] = useState<TimeFilter>("dashboard_oneMonth")

  // Sample data - replace with actual API calls
  const summaryData = {
    revenue: { value: "1000,00", change: "12%" },
    earnings: { value: "1000,00", change: "12%" },
    commission: { value: "1000,00", change: "12%" },
  }

  const series = [
    {
      name: t("dashboard_current"),
      data: [
        15, 20, 18, 25, 30, 45, 55, 65, 75, 85, 80, 70, 60, 50, 45, 40, 35,
        30, 35, 40, 45, 50, 55, 50, 45, 40, 35, 30, 25, 20,
      ],
    },
    {
      name: t("dashboard_previous"),
      data: [
        10, 15, 12, 18, 20, 30, 40, 50, 60, 70, 75, 65, 55, 45, 40, 35, 30,
        25, 30, 35, 40, 45, 40, 35, 30, 25, 20, 15, 12, 10,
      ],
    },
  ]

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "area",
      height: 350,
      toolbar: {
        show: false,
      },
      fontFamily: "Inter, sans-serif",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    colors: ["#556EE6", "#F1B44C"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: [0.5, 0.5],
        opacityTo: [0.02, 0.02],
        stops: [0, 100],
      },
    },
    xaxis: {
      categories: Array.from({ length: 30 }, (_, i) => i + 1),
      title: {
        text: "January",
        style: {
          fontSize: "12px",
          fontWeight: 500,
          color: "#6B7280",
        },
      },
      labels: {
        style: {
          fontSize: "11px",
          colors: ["#9CA3AF"],
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      title: {
        text: "Thousand QAR",
        style: {
          fontSize: "12px",
          fontWeight: 500,
          color: "#6B7280",
        },
      },
      labels: {
        style: {
          fontSize: "11px",
          colors: ["#9CA3AF"],
        },
        formatter: value => value.toString(),
      },
      min: 0,
      max: 90,
      tickAmount: 9,
    },
    grid: {
      borderColor: "#F3F4F6",
      strokeDashArray: 4,
      row: {
        colors: ["transparent", "transparent"],
        opacity: 0.5,
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
      markers: {
        radius: 12,
        strokeWidth: 0,
      },
      itemMargin: {
        horizontal: 16,
        vertical: 0,
      },
    },
    tooltip: {
      theme: "light",
      style: {
        fontSize: "12px",
      },
      x: {
        format: "dd MMM",
      },
      y: {
        formatter: value => `${value},000 QAR`,
      },
    },
    markers: {
      size: 0,
      hover: {
        size: 6,
      },
    },
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex justify-content-between align-items-center">
        <h4
          className="font-size-16 fw-semibold mb-0"
          style={{ color: "#111827" }}
        >
          {t("dashboard_revenueEarningsTrend")}
        </h4>
        <div className="d-flex gap-2">
          {(
            [
              "dashboard_all",
              "dashboard_oneMonth",
              "dashboard_sixMonths",
              "dashboard_oneYear",
            ] as TimeFilter[]
          ).map(filter => (
            <TimeFilterButton
              key={filter}
              label={filter}
              isActive={activeFilter === filter}
              onClick={() => setActiveFilter(filter)}
            />
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="g-4">
        <Col>
          <SummaryCard
            title={t("dashboard_revenue")}
            value={summaryData.revenue.value}
            change={`${summaryData.revenue.change}`}
          />
        </Col>
        <Col>
          <SummaryCard
            title={t("dashboard_earnings")}
            value={summaryData.earnings.value}
            change={`${summaryData.earnings.change}`}
          />
        </Col>
        <Col>
          <SummaryCard
            title={t("dashboard_Commission")}
            value={summaryData.commission.value}
            change={`${summaryData.commission.change}`}
          />
        </Col>
      </Row>

      {/* Chart */}
      <div
        style={{
          boxShadow: "0px 1px 2px 0px #38414A26",
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "16px",
        }}
      >
        <ReactApexChart
          options={options}
          series={series}
          type="area"
          height="300"
        />
      </div>
    </div>
  )
}
